/**
 * Runtime configuration loader.
 *
 * In production (SWA), deploy-swa.ps1 writes a /config.json into the build
 * output before deploying. This file is served by the CDN and loaded here at
 * module init time. Using a runtime file means the web app can be built once
 * and deployed to any environment without a rebuild.
 *
 * In local development, /config.json is not present (CRA dev server returns
 * 404 for unknown paths), so we fall back to process.env values from .env.
 */

export interface AppConfig {
	apiBaseUrl: string;
	functionKey: string;
}

let resolvedConfig: AppConfig | null = null;

const configPromise: Promise<AppConfig> = (async (): Promise<AppConfig> => {
	try {
		const res = await fetch('/config.json');
		const contentType = res.headers.get('content-type') || '';
		if (res.ok && contentType.includes('application/json')) {
			const json = await res.json();
			resolvedConfig = {
				apiBaseUrl: (json.REACT_APP_API_BASE_URL as string) || process.env.REACT_APP_API_BASE_URL || '',
				functionKey: (json.REACT_APP_FUNCTION_KEY as string) || process.env.REACT_APP_FUNCTION_KEY || '',
			};
			return resolvedConfig;
		}
	} catch {
		// /config.json unavailable (local dev or network error) — fall through
	}

	resolvedConfig = {
		apiBaseUrl: process.env.REACT_APP_API_BASE_URL || '',
		functionKey: process.env.REACT_APP_FUNCTION_KEY || '',
	};
	return resolvedConfig;
})();

/**
 * Returns the resolved AppConfig. Always resolves — falls back to process.env
 * when /config.json is unavailable.
 */
export async function getAppConfig(): Promise<AppConfig> {
	return configPromise;
}

/**
 * Synchronous accessor. Returns the config if already resolved, otherwise
 * falls back to process.env. Safe to call after the first awaited API call
 * has already triggered config resolution.
 */
export function getAppConfigSync(): AppConfig {
	return (
		resolvedConfig ?? {
			apiBaseUrl: process.env.REACT_APP_API_BASE_URL || '',
			functionKey: process.env.REACT_APP_FUNCTION_KEY || '',
		}
	);
}
